import React, { useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { tw } from '../../../utils/tailwind';
import { SelectList } from '../../forms/select-list';
import { TIP_PAYMENT_METHODS, twCb, useT } from '../../../utils/helper';
import { ModalConfirmation } from '../../shared/modals/modal-confirmation';
import { TouchableOpacity as Button } from '../../forms';
import { Input } from '../forms/input';
import { useTipInvoiceForm } from '../../../forms/use-tip-invoice-form.hook';
import { useTipInvoiceMutations } from '../../../api/hooks/cash-register/invoices/use-tip-invoice-mutations';
import { useAlert } from '../../../contexs/alert-context';

interface ModalCreateTipInvoiceProps {
  isVisible: boolean;
  content: string;
  onModalHide: () => void;
}

export const ModalCreateTipInvoice = (props: ModalCreateTipInvoiceProps) => {
  // Standard Hooks
  const t = useT('forms');
  const alert = useAlert();
  // Custom Hooks
  const form = useTipInvoiceForm(() => handleCreateTipInvoice());
  const { createTipInvoice } = useTipInvoiceMutations();

  // Callbacks
  const handleCreateTipInvoice = () => {
    createTipInvoice
      .mutateAsync(form.values)
      .then(() => {
        props.onModalHide();
        alert.show(t('successfullyCreated').toString());
      })
      .catch(() => {
        alert.show(t('errorCreating').toString());
      });
  };

  const formSubmitted = form.submitCount > 0;
  const paymentMethodOptions = useMemo(
    () =>
      TIP_PAYMENT_METHODS.map(method => ({
        label: t(`paymentMethods.${method}`).toString(),
        value: method,
      })),
    [],
  );

  // Reset form when modal is closed
  useEffect(() => {
    form.resetForm();
  }, [props.isVisible]);

  return (
    <ModalConfirmation
      isVisible={props.isVisible}
      content={props.content}
      onModalHide={props.onModalHide}>
      <View style={tw`flex-1 w-full z-10`}>
        <>
          <View style={tw`gap-y-3 z-10`}>
            {/* Tip amount */}
            <Input
              autoFocus={true}
              placeholder={t('tipAmountInEuro').toString()}
              value={form.values.tip_amount?.toString() || ''} // has to be string
              onChangeText={form.handleChange('tip_amount')}
              onBlur={form.handleBlur('tip_amount')}
              error={
                formSubmitted || form.touched.tip_amount
                  ? form.errors.tip_amount
                  : null
              }
            />

            {/* Payment method */}
            <SelectList
              setSelected={selectedOption => {
                form.setFieldValue('payment_method', selectedOption.key);
              }}
              labelKey="label"
              valueKey="value"
              data={paymentMethodOptions}
              defaultOption={form.values.payment_method}
              placeholder={t('selectPaymentMethod').toString()}
              dropdownStyles={tw`top-[60px]`}
              errorMessage={
                formSubmitted || form.touched.payment_method
                  ? form.errors.payment_method
                  : ''
              }
            />
          </View>

          <View style={tw`gap-3 my-3 w-full`}>
            {/* Create button */}
            <Button
              label={t('create')}
              disabled={createTipInvoice.isLoading}
              onPress={() => form.submitForm()}
              extraStyle={twCb('py-5', form.isValid ? '' : 'opacity-50')}
              extraLabelStyle="leading-none font-medium text-white"
            />
          </View>
        </>
      </View>
    </ModalConfirmation>
  );
};
